import { useEffect, useState, useRef, useCallback } from "react";
import HearingTestButton from "../../../components/hearingTestButton/hearingTestButton";
import PillTitle from "../../../components/pillTitle/pillTitle";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import type { Ear } from "../../../type/tonalTest";
import type { SRTPanelProps, VerbalPanelState, VerbalWordRecord } from "../../../type/verbalTest";
import { hasSpeechRecognitionSupport } from "../../../type/verbalTest";
import { speakToEar } from "../../../utils/speakToEar";
import { SRT_RESPONSE_TIMEOUT_MS, SRT_WORDS_PER_LEVEL, HEADPHONE_VOLUME_COMPENSATION } from "../../../utils/config";
import wordsDb from "../../../assets/words/words_pl.json";

// ── dB scale (0 to 100 in 5 dB steps, 21 levels) ─────────────────────────────
const dBLevels = Array.from({ length: 21 }, (_, i) => i * 5);
const STARTING_DB = 60;
const STARTING_IDX = dBLevels.indexOf(STARTING_DB); // 12

const speechVolume = (dB: number): number => {
  if (dB === 0) return 0.001 * HEADPHONE_VOLUME_COMPENSATION;
  return Math.min(1.0, Math.max(0.001, 0.5 * HEADPHONE_VOLUME_COMPENSATION * Math.pow(10, (dB - 75) / 20)));
};

// ── Phonetic similarity helpers for Polish bisyllabic words ───────────────────
const POLISH_VOWELS = new Set(["a", "e", "i", "o", "u", "y", "ą", "ę", "ó"]);

function extractRhyme(word: string): string {
  const w = word.toLowerCase();
  for (let i = 0; i < w.length; i++) {
    if (POLISH_VOWELS.has(w[i])) return w.slice(i);
  }
  return w;
}

function extractVowel(word: string): string {
  for (const ch of word.toLowerCase()) {
    if (POLISH_VOWELS.has(ch)) return ch;
  }
  return "";
}

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function phoneticScore(target: string, candidate: string): number {
  const t = target.toLowerCase();
  const c = candidate.toLowerCase();
  let score = 0;
  const tRhyme = extractRhyme(t);
  const cRhyme = extractRhyme(c);
  if (tRhyme === cRhyme) score += 100;
  else if (tRhyme.startsWith(cRhyme) || cRhyme.startsWith(tRhyme)) score += 50;
  if (extractVowel(t) === extractVowel(c)) score += 30;
  if (t[t.length - 1] === c[c.length - 1]) score += 20;
  if (t[0] === c[0]) score += 10;
  score += Math.max(0, 20 - levenshtein(t, c) * 5);
  return score;
}

// Pool of all SRT words across all lists, used for distractor selection
const allSrtWords: string[] = wordsDb.srt_lists.flatMap(l => l.words);
const allSrtSentences: string[] = wordsDb.srt_sentence_lists.flatMap(l => l.sentences);

function buildChoices(target: string): string[] {
  const others = allSrtWords.filter(w => w.toLowerCase() !== target.toLowerCase());
  const scored = others.map(w => ({
    word: w,
    score: phoneticScore(target, w) + Math.random() * 5,
  }));
  scored.sort((a, b) => b.score - a.score);
  const distractors = scored.slice(0, 3).map(s => s.word);
  return [...distractors, target].sort(() => Math.random() - 0.5);
}

function buildSentenceChoices(target: string): string[] {
  const others = allSrtSentences.filter(s => s !== target);
  const shuffled = [...others].sort(() => Math.random() - 0.5).slice(0, 3);
  return [...shuffled, target].sort(() => Math.random() - 0.5);
}

// ── Sentence mic-matching helpers ─────────────────────────────────────────────
const SENTENCE_STOP_WORDS_SRT = new Set(["w", "z", "na", "do", "po", "ze", "i", "a", "się", "że", "to", "jest", "są", "o", "u", "od"]);

function contentWordsSRT(sentence: string): string[] {
  return sentence.toLowerCase()
    .replace(/[.,!?;:]/g, "")
    .split(/\s+/)
    .filter(w => w.length > 2 && !SENTENCE_STOP_WORDS_SRT.has(w));
}

function isSentenceMatchSRT(target: string, transcripts: string[]): boolean {
  const tWords = contentWordsSRT(target);
  if (tWords.length === 0) return false;
  return transcripts.some(tr => {
    const matched = tWords.filter(w => tr.includes(w));
    return matched.length / tWords.length >= 0.5;
  });
}

function getSRTWords(ear: Ear): string[] {
  const listIdx = ear === "left" ? 0 : 1;
  return [...(wordsDb.srt_lists[listIdx]?.words ?? wordsDb.srt_lists[0].words)];
}

function getSRTSentences(ear: Ear): string[] {
  const listIdx = ear === "left" ? 0 : 1;
  return [...(wordsDb.srt_sentence_lists[listIdx]?.sentences ?? wordsDb.srt_sentence_lists[0].sentences)];
}

// ── State machine ─────────────────────────────────────────────────────────────
export default function VerbalSRTPanel({ ear, useMicrophone, useSentences, onComplete, onCancel, user }: SRTPanelProps) {
  const { t } = useI18n();
  const words = useRef(useSentences ? getSRTSentences(ear) : getSRTWords(ear));

  // ── Adaptive dB navigation ────────────────────────────────────────────────
  const [volIndex, setVolIndex]       = useState(STARTING_IDX);
  const [mode, setMode]               = useState<"descending" | "ascending">("descending");
  const [lastHeardDb, setLastHeardDb] = useState<number | undefined>(undefined);

  // ── Within-level tracking (SRT_WORDS_PER_LEVEL words per dB level) ─────
  const [wordIndexInLevel, setWordIndexInLevel] = useState(0);
  const [correctInLevel, setCorrectInLevel]     = useState(0);
  const [globalWordIndex, setGlobalWordIndex]   = useState(0); // cycles through words array

  // ── Results & UI ──────────────────────────────────────────────────────────
  const [allWordRecords, setAllWordRecords] = useState<VerbalWordRecord[]>([]);
  const [state, setState]                   = useState<VerbalPanelState>({ phase: "playing" });
  const [isFinished, setIsFinished]         = useState(false);
  const [timeLeft, setTimeLeft]             = useState<number | null>(null);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const cancelSpeech         = useRef<(() => void) | null>(null);
  const feedbackTimer        = useRef<number | null>(null);
  const responseTimeoutRef   = useRef<number | null>(null);
  const countdownIntervalRef = useRef<number | null>(null);
  const recognitionRef       = useRef<SpeechRecognition | null>(null);
  const micResultReceivedRef = useRef(false);

  const currentDb   = dBLevels[Math.min(volIndex, dBLevels.length - 1)];
  const currentWord = words.current[globalWordIndex % words.current.length];
  const canUseMic   = useMicrophone && hasSpeechRecognitionSupport();

  const clearResponseTimeout = () => {
    if (responseTimeoutRef.current !== null) {
      window.clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    if (countdownIntervalRef.current !== null) {
      window.clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setTimeLeft(null);
  };

  const startCountdown = (ms: number) => {
    const totalSec = Math.ceil(ms / 1000);
    setTimeLeft(totalSec);
    if (countdownIntervalRef.current !== null) window.clearInterval(countdownIntervalRef.current);
    countdownIntervalRef.current = window.setInterval(() => {
      setTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          window.clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Apply adaptive step after all words in a dB level are answered ────────
  // heard = (correctInLevel / SRT_WORDS_PER_LEVEL) >= 0.5
  const commitLevelStep = useCallback((
    heard: boolean,
    volIdx: number,
    modeVal: "descending" | "ascending",
    lastHeard: number | undefined,
    newRecords: VerbalWordRecord[],
  ) => {
    const heardDb = dBLevels[volIdx];

    if (heard) {
      if (modeVal === "ascending") {
        setIsFinished(true);
        setTimeout(() => onComplete(ear, heardDb, newRecords), 300);
      } else if (volIdx <= 0) {
        setIsFinished(true);
        setTimeout(() => onComplete(ear, heardDb, newRecords), 300);
      } else {
        setLastHeardDb(heardDb);
        setMode("descending");
        setVolIndex(Math.max(0, volIdx - 1));
        setWordIndexInLevel(0);
        setCorrectInLevel(0);
        setState({ phase: "playing" });
      }
    } else {
      if (modeVal === "descending") {
        if (lastHeard !== undefined) {
          setIsFinished(true);
          setTimeout(() => onComplete(ear, lastHeard, newRecords), 300);
        } else if (volIdx >= dBLevels.length - 1) {
          setIsFinished(true);
          setTimeout(() => onComplete(ear, dBLevels[dBLevels.length - 1], newRecords), 300);
        } else {
          setMode("ascending");
          setVolIndex(Math.min(dBLevels.length - 1, volIdx + 1));
          setWordIndexInLevel(0);
          setCorrectInLevel(0);
          setState({ phase: "playing" });
        }
      } else {
        // Ascending, not heard
        if (volIdx >= dBLevels.length - 1) {
          setIsFinished(true);
          setTimeout(() => onComplete(ear, dBLevels[dBLevels.length - 1], newRecords), 300);
        } else {
          setVolIndex(volIdx + 1);
          setWordIndexInLevel(0);
          setCorrectInLevel(0);
          setState({ phase: "playing" });
        }
      }
    }
  }, [ear, onComplete]);

  // ── Microphone recognition ─────────────────────────────────────────────────
  const startMicRecognition = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!SpeechRecognitionAPI) return;

    const rec = new SpeechRecognitionAPI();
    rec.lang = "pl-PL";
    rec.interimResults = false;
    rec.maxAlternatives = 3;

    rec.onresult = (event: SpeechRecognitionEvent) => {
      micResultReceivedRef.current = true;
      clearResponseTimeout();
      const transcripts = Array.from({ length: event.results[0].length }, (_, i) =>
        event.results[0][i].transcript.trim().toLowerCase()
      );
      const correct = useSentences
        ? isSentenceMatchSRT(currentWord, transcripts)
        : transcripts.some(tr => tr.trim() === currentWord.toLowerCase());
      recordAnswer(correct, transcripts[0] ?? "");
    };

    rec.onerror = (event: Event) => {
      if ((event as SpeechRecognitionEvent & { error: string }).error === "no-speech") return;
      micResultReceivedRef.current = true;
      clearResponseTimeout();
      recordAnswer(false, "");
    };

    rec.onend = () => {
      recognitionRef.current = null;
      if (!micResultReceivedRef.current) {
        if (responseTimeoutRef.current !== null) {
          startMicRecognition();
        } else {
          micResultReceivedRef.current = true;
          recordAnswer(false, "");
        }
      }
    };

    recognitionRef.current = rec;
    try { rec.start(); } catch { /* already started */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord]);

  // ── Record one word answer, advance within level or commit level step ─────
  const recordAnswer = useCallback((correct: boolean, response = "") => {
    clearResponseTimeout();
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      recognitionRef.current = null;
    }

    const record: VerbalWordRecord = { word: currentWord, dB: currentDb, heard: correct, response };
    const newRecords        = [...allWordRecords, record];
    const newCorrectInLevel = correctInLevel + (correct ? 1 : 0);
    const nextWordInLevel   = wordIndexInLevel + 1;

    setAllWordRecords(newRecords);
    setState({ phase: "feedback", correct, correctWord: currentWord });

    feedbackTimer.current = window.setTimeout(() => {
      setGlobalWordIndex(prev => prev + 1);

      if (nextWordInLevel >= SRT_WORDS_PER_LEVEL) {
        // Level complete — average ≥ 50 % counts as "heard at this dB"
        const heard = newCorrectInLevel / SRT_WORDS_PER_LEVEL >= 0.5;
        setWordIndexInLevel(0);
        setCorrectInLevel(0);
        commitLevelStep(heard, volIndex, mode, lastHeardDb, newRecords);
      } else {
        setWordIndexInLevel(nextWordInLevel);
        setCorrectInLevel(newCorrectInLevel);
        setState({ phase: "playing" });
      }
    }, 1000);
  }, [allWordRecords, correctInLevel, currentDb, currentWord, wordIndexInLevel, volIndex, mode, lastHeardDb, commitLevelStep]);

  // ── Play word via TTS routed to tested ear ────────────────────────────────
  const playCurrentWord = useCallback(() => {
    cancelSpeech.current?.();
    cancelSpeech.current = speakToEar(
      currentWord,
      speechVolume(currentDb),
      ear,
      () => {
        cancelSpeech.current = null;
        clearResponseTimeout();
        if (canUseMic) {
          setState({ phase: "awaiting_mic" });
          micResultReceivedRef.current = false;
          startMicRecognition();
          responseTimeoutRef.current = window.setTimeout(() => {
            responseTimeoutRef.current = null;
            if (recognitionRef.current) {
              try { recognitionRef.current.stop(); } catch { /* ignore */ }
            } else {
              if (!micResultReceivedRef.current) {
                micResultReceivedRef.current = true;
                recordAnswer(false, "");
              }
            }
          }, SRT_RESPONSE_TIMEOUT_MS);
          startCountdown(SRT_RESPONSE_TIMEOUT_MS);
        } else {
          setState({ phase: "awaiting_choice", choices: useSentences ? buildSentenceChoices(currentWord) : buildChoices(currentWord) });
          responseTimeoutRef.current = window.setTimeout(() => {
            responseTimeoutRef.current = null;
            recordAnswer(false, "");
          }, SRT_RESPONSE_TIMEOUT_MS);
          startCountdown(SRT_RESPONSE_TIMEOUT_MS);
        }
      },
      () => {
        cancelSpeech.current = null;
        clearResponseTimeout();
        recordAnswer(false, "");
      },
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWord, currentDb, ear, canUseMic, recordAnswer]);

  // ── Play word whenever phase returns to "playing" ─────────────────────────
  useEffect(() => {
    if (isFinished || state.phase !== "playing") return;
    playCurrentWord();
    return () => { cancelSpeech.current?.(); cancelSpeech.current = null; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalWordIndex, volIndex, isFinished, state.phase]);

  // ── Cleanup on unmount ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      cancelSpeech.current?.();
      cancelSpeech.current = null;
      if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
      clearResponseTimeout();
    };
  }, []);

  const handleChoiceClick = (choice: string) => {
    if (state.phase !== "awaiting_choice") return;
    const correct = useSentences
      ? choice === currentWord
      : choice.toLowerCase() === currentWord.toLowerCase();
    recordAnswer(correct, choice);
  };

  // ⌨️ keyboard shortcut: Esc/N = "Nie słyszę / Nie rozumiem"
  useEffect(() => {
    const canAnswerNo = state.phase === "awaiting_choice" || state.phase === "awaiting_mic";
    if (!canAnswerNo) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Escape" || e.key === "Escape" || e.key.toLowerCase() === "n") {
        e.preventDefault();
        recordAnswer(false, "");
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [state.phase, recordAnswer]);

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="verbal-test__panel-content">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
            <div className="test-panel__summary">
              <PillTitle as="div" variant="blue-darkner" iconClassName="bi bi-mic-fill" text={t("Test SRT")} />
              <h2 className="mb-1">
                {t("Próg rozumienia mowy")} — {ear === "left" ? t("Lewe ucho") : t("Prawe ucho")}
              </h2>
              <p className="mb-0">
                {canUseMic
                  ? t("Powtórz usłyszane słowo do mikrofonu. MÓW GŁOŚNO i WYRAŹNIE !!!") 
                  : t("Wybierz słowo, które usłyszałeś/aś.")}
              </p>
              <div className="d-flex flex-wrap gap-2 mt-2">
                {user?.admin === true && (
                  <>
                    <span className="badge rounded-pill text-bg-light">{currentWord}</span>
                    <span className="badge rounded-pill text-bg-light">{currentDb} dB</span>
                  </>
                )}
                <span className="badge rounded-pill text-bg-light">
                  {wordIndexInLevel + 1}/{SRT_WORDS_PER_LEVEL} {t("słów")}
                </span>
              </div>
            </div>
            <div className="text-md-end">
              <span className="small text-uppercase">{t("Głośność")}</span>{" "}
              <strong>{currentDb} dB</strong>
            </div>
          </div>

          {/* ── State-dependent UI ──────────────────────────────────────────── */}
          {state.phase === "playing" && (
            <div className="verbal-test__word-display" aria-live="polite">
              <span className="verbal-test__speaking-icon"><i className="bi bi-volume-up-fill" /></span>
              <span className="verbal-test__word-status">{t("Słowo odtwarzane…")}</span>
            </div>
          )}

          {state.phase === "awaiting_mic" && (
            <div className="verbal-test__mic-waiting">
              <div className="verbal-test__mic-animation">
                <i className="bi bi-mic-fill" />
              </div>
              <p className="verbal-test__word-status">{t("Powtórz słowo do mikrofonu…")}</p>
              {timeLeft !== null && (
                <div className="verbal-wrs__countdown">
                  <svg className="verbal-wrs__countdown-svg" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.9"
                      fill="none" stroke="#f4c56c" strokeWidth="3"
                      strokeDasharray={`${(timeLeft / Math.ceil(SRT_RESPONSE_TIMEOUT_MS / 1000)) * 100} 100`}
                      strokeLinecap="round"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <span className="verbal-wrs__countdown-value">{timeLeft}</span>
                </div>
              )}
              <button
                type="button"
                className="btn btn-sm btn-outline-light btn-switch-to-buttons"
                onClick={() => {
                  micResultReceivedRef.current = true;
                  clearResponseTimeout();
                  if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
                  setState({ phase: "awaiting_choice", choices: buildChoices(currentWord) });
                  responseTimeoutRef.current = window.setTimeout(() => {
                    responseTimeoutRef.current = null;
                    recordAnswer(false, "");
                  }, SRT_RESPONSE_TIMEOUT_MS);
                  startCountdown(SRT_RESPONSE_TIMEOUT_MS);
                }}
              >
                {t("Użyj przycisków")}
              </button>
            </div>
          )}

          {state.phase === "awaiting_choice" && (
            <>
              {timeLeft !== null && (
                <div className="verbal-wrs__countdown verbal-wrs__countdown--bar">
                  <svg className="verbal-wrs__countdown-svg" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="15.9" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="3" />
                    <circle
                      cx="18" cy="18" r="15.9"
                      fill="none" stroke="#f4c56c" strokeWidth="3"
                      strokeDasharray={`${(timeLeft / Math.ceil(SRT_RESPONSE_TIMEOUT_MS / 1000)) * 100} 100`}
                      strokeLinecap="round"
                      transform="rotate(-90 18 18)"
                    />
                  </svg>
                  <span className="verbal-wrs__countdown-value">{timeLeft}</span>
                </div>
              )}
              <div className="verbal-wrs__choices">
                {state.choices.map(choice => (
                  <button
                    key={choice}
                    type="button"
                    className="verbal-wrs__choice-btn"
                    onClick={() => handleChoiceClick(choice)}
                  >
                    {choice}
                  </button>
                ))}
              </div>
            </>
          )}

          {state.phase === "feedback" && (
            <div className={`verbal-wrs__feedback verbal-wrs__feedback--${state.correct ? "correct" : "incorrect"}`}>
              <i className={`bi ${state.correct ? "bi-check-circle-fill" : "bi-x-circle-fill"}`} />
              <span>
                {state.correct
                  ? t("Dobrze!")
                  : `${t("Słowo to:")} ${state.correctWord}`}
              </span>
            </div>
          )}

        </div>

        <div className="d-flex flex-column flex-sm-row justify-content-end gap-3">
          {(state.phase === "awaiting_choice" || state.phase === "awaiting_mic") && (
            <HearingTestButton
              type="button"
              className="btn btn-outline-light btn-lg fs-5 flex-grow-1 text-danger"
              iconClassName="bi bi-x-circle"
              onClick={() => recordAnswer(false, "")}
            >
              {t("Nie słyszę / Nie rozumiem")} (Esc/N)
            </HearingTestButton>
          )}
          <HearingTestButton
            type="button"
            className="btn btn-outline-warning btn-lg fs-5"
            iconClassName="bi bi-stop-circle-fill"
            onClick={() => { window.speechSynthesis.cancel(); onCancel(allWordRecords); }}
          >
            {t("Zakończ test")}
          </HearingTestButton>
        </div>
      </div>
    </div>
  );
}
